// CustomerLocation.jsx
// Opened in a new tab from Customer.jsx's "i" action icon.
// Split search pill + dropdown (auto-detect + popular localities), magicpin-style.
//
// Search box only surfaces cities we actually have a store in (fetched via
// useCityDirectory) - no raw Google Places predictions. That's intentional:
// Google's autocomplete would suggest places like Azamgarh that we don't
// service at all, so instead of filtering its predictions we just don't
// show them - only the store-backed directory.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, LocateFixed, Loader2 } from "lucide-react";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useCityServiceCheck from "../../hooks/useCityServiceCheck";
import useCityDirectory from "../../hooks/useCityDirectory";
import StoreCategories from "./StoreCategories";
import StoreDiscoverList from "./StoreDiscoverList";
import NearbyStoresList from "./NearbyStoresList";
import { loadStoreCategories, resetStoreCategories, setSelectedCategory } from "../../store/storeCategoriesSlice";
import { resetStoreDiscover } from "../../store/storeSlices/storeDiscoverSlice";
import { resetStoreNearby } from "../../store/storeNearbySlice";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { useDispatch } from "react-redux";
import MembershipStatusCard from "./MembershipStatusCard";
import MembershipPage from "./MembershipPage";
import { useNavigate } from "react-router-dom";
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import { getMembershipStatus, getMembershipCredit, buildOrderMembership } from "../../utils/membershipStatus";
import { saveCheckoutOrder } from "../../utils/checkoutOrder";
import WebHeader from "./WebHeader";
// Set VITE_GOOGLE_MAPS_API_KEY in your .env — same key already used by
// NewPlaceAutocomplete / LocationPickerModal elsewhere in the app.
// Still needed here for Geocoder (reverse-geocoding auto-detect coords,
// and forward-geocoding a picked city name into a full address + coords).
const GOOGLE_MAPS_API_KEY = "AIzaSyD1683tMzeZXGFhxXEr96VwjffvcrmVWTc";
const CHECKOUT_PATH = "/web/store-payment";

// Only used if the backend's serviceable-cities list hasn't loaded yet
// (or comes back empty) - the real default list now comes from the store.
const FALLBACK_LOCALITIES = [
  "New Delhi",
  "Manipur",
  "Uttar Pradesh",
  "Noida",
  "Mumbai",
  "Bangalore",
];

const extractComponent = (components, type) =>
  components.find((c) => c.types.includes(type))?.long_name || "";

// Normalizes a Google Geocoder result into one shape.
const buildLocationData = (place, coordsOverride) => {
  const components = place.address_components || [];
  const coords = coordsOverride || {
    lat: place.geometry?.location?.lat?.() ?? place.geometry?.location?.lat,
    lng: place.geometry?.location?.lng?.() ?? place.geometry?.location?.lng,
  };
  return {
    latitude: coords.lat,
    longitude: coords.lng,
    formattedAddress: place.formatted_address,
    street: extractComponent(components, "route"),
    streetNumber: extractComponent(components, "street_number"),
    locality: extractComponent(components, "locality"),
    sublocality: extractComponent(components, "sublocality"),
    city:
      extractComponent(components, "locality") ||
      extractComponent(components, "administrative_area_level_2"),
    state: extractComponent(components, "administrative_area_level_1"),
    country: extractComponent(components, "country"),
    postalCode: extractComponent(components, "postal_code"),
    neighborhood: extractComponent(components, "neighborhood"),
    displayAddress: place.formatted_address,
    shortAddress: `${extractComponent(components, "locality")}, ${extractComponent(
      components,
      "administrative_area_level_1"
    )}`,
    area:
      extractComponent(components, "sublocality") ||
      extractComponent(components, "neighborhood") ||
      extractComponent(components, "locality"),
      placeId: place.place_id,
  };
};

export default function Landing() {
useDocumentTitle("Welcome to Admin | Set Customer Location");
const LANDING_STATE_KEY = "goerone_landing_state";
const navigate = useNavigate()
const [customer, setCustomer] = useState({ id: "", name: "Customer", mobile:"" });

useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setCustomer({
        id: params.get("id") || "",
        name: params.get("name") || "Customer",
        mobile: params.get("mobile") || "",
    });
}, []);

// Secondary "GoerOne" customer auth (separate from the app's main admin auth) -
// starts the test-login session for this tab once customer.id/mobile are known,
// and every other /web page reads the same session via useGoerOneAuth().
const { user: testUser, loginError,refreshUser  } = useGoerOneAuth(
    customer.id && customer.mobile ? { id: customer.id, mobile: customer.mobile } : null
);

useEffect(() => { refreshUser(); }, [refreshUser]);

useEffect(() => {
    if (loginError) toastr.error(loginError);
}, [loginError]);

const [membershipOpen, setMembershipOpen] = useState(false);
const membershipStatus = getMembershipStatus(testUser);

const membershipCredit = getMembershipCredit(testUser); // unused value of the active plan, taken off an upgrade

const handleMembershipSelect = (program, action) => {
    const order = {
        // fee already has the upgrade credit taken off (see buildOrderMembership)
        membership: buildOrderMembership({ program, action }, membershipCredit),
        successPath: "/web/home",
    };
    saveCheckoutOrder(order);
    setMembershipOpen(false);
    navigate(CHECKOUT_PATH, { state: { order } });
};

const getSavedLandingState = () => {
  try {
    const saved = sessionStorage.getItem(LANDING_STATE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const savedLandingState = getSavedLandingState();

const [selectedLocation, setSelectedLocation] = useState(
  savedLandingState?.selectedLocation || null
);

const [saving, setSaving] = useState(false);

const [confirmed, setConfirmed] = useState(
  savedLandingState?.confirmed || false
);

const [storeCityName, setStoreCityName] = useState(
  savedLandingState?.storeCityName || ""
);

const [query, setQuery] = useState(
  savedLandingState?.query || ""
);


useEffect(() => {
  if (!selectedLocation) return;

  sessionStorage.setItem(
    LANDING_STATE_KEY,
    JSON.stringify({
      selectedLocation,
      storeCityName,
      confirmed,
      query,
    })
  );
}, [selectedLocation, storeCityName, confirmed, query]);

  const dispatch = useDispatch();

  // City name the discover/category endpoints filter on.
  const activeCity = storeCityName;

// Categories only make sense once we know where the customer is.
useEffect(() => {
    if (selectedLocation) dispatch(loadStoreCategories());
}, [selectedLocation, dispatch]);

// useEffect(() => () => dispatch(resetStoreCategories()), [dispatch]);
// useEffect(() => () => dispatch(resetStoreDiscover()), [dispatch]);
// useEffect(() => () => dispatch(resetStoreNearby()), [dispatch]);
  // Auto-detect flow only: the one path that isn't already store-backed,
  // so it's the only one that needs the real serviceability-check API.
  const { checking: checkingService, notServiceable, checkCity, reset: resetService } = useCityServiceCheck();

  // Store-backed directory: popular cities + search-as-you-type. Anything
  // that comes out of here is guaranteed serviceable.
  const {
    topCities,
    topCitiesLoading,
    searchResults: citySuggestions,
    searching: searchingCities,
    searchCities,
  } = useCityDirectory({ topLimit: 5 });

  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [localityLoading, setLocalityLoading] = useState("");

  const wrapperRef = useRef(null);
  const geocoder = useRef(null);

  // Load Google Maps script once (Geocoder only - no Places autocomplete).
  useEffect(() => {
    const initServices = () => {
      geocoder.current = new window.google.maps.Geocoder();
      setIsGoogleLoaded(true);
    };
    if (window.google?.maps) {
      initServices();
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = initServices;
    document.head.appendChild(script);
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    resetService();

    // Cities we actually have a store in, matching what's typed so far -
    // the only source of search suggestions now.
    searchCities(value);
  };

  const selectPlace = useCallback(
    (locationData, label) => {
      setQuery(label);
      setIsOpen(false);
      setSelectedLocation(null);
      setConfirmed(false);
      setStoreCityName("");
      dispatch(resetStoreCategories());
      dispatch(resetStoreDiscover());
      dispatch(resetStoreNearby());
      resetService();
      setSelectedLocation(locationData);
      // `label` is the store-backed city.cityName from useCityDirectory,
      // not a Google-parsed field — this is the name the backend matched.
      setStoreCityName(label);
    },
    [resetService, dispatch]
  );

  const handleAutoDetect = () => {
    if (!navigator.geolocation) {
      toastr.error("Geolocation isn't supported by this browser.");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        geocoder.current.geocode({ location: coords }, async (results, status) => {
          setIsLocating(false);
          if (status === "OK" && results?.[0]) {
            const locationData = buildLocationData(results[0], coords);

            setQuery("Current location");
            setIsOpen(false);
            setSelectedLocation(null);
            setConfirmed(false);
            setStoreCityName("");
            dispatch(resetStoreCategories());
            dispatch(resetStoreDiscover());
            dispatch(resetStoreNearby());

            // Only auto-detect hits the real serviceability-check API,
            // since it's the only source that isn't already store-backed.
            const { serviceable, matchedCity } = await checkCity(locationData);
            if (serviceable) {
              setSelectedLocation(locationData);
              // Prefer the backend's resolved static_cities.cityName over
              // Google's parsed city — falls back only when unresolved
              // (matchedCity is null, e.g. no city on the address at all).
              setStoreCityName(matchedCity?.cityName || locationData.city);
            }
          } else {
            toastr.error("Couldn't find an address for your current location.");
          }
        });
      },
      () => {
        setIsLocating(false);
        toastr.error("Location access denied. Enable it in your browser settings.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // `city` is either a plain locality string (fallback list) or a
  // { id, cityName, fullRegionName } object from the serviceable-cities API.
  // Either way it's store-backed, so no serviceability check needed here.
  const handleLocalityClick = (city) => {
    const label = typeof city === "string" ? city : city.cityName;
    const key = typeof city === "string" ? city : city.id;

    setLocalityLoading(key);
    geocoder.current.geocode({ address: `${label}, India` }, (results, status) => {
      setLocalityLoading("");
      if (status === "OK" && results?.[0]) {
        selectPlace(buildLocationData(results[0]), label);
      } else {
        toastr.error(`Couldn't find "${label}".`);
      }
    });
  };

  const handleConfirm = async () => {
    if (!selectedLocation) return;
    setSaving(true);
    try {
      // TODO: wire to the real backend endpoint that stores the customer's location, e.g.
      // await updateCustomerLocation(customer.id, selectedLocation);

      // Let the Customer list tab (the opener) know a location was picked, in case it
      // wants to refresh or show it without a page reload.
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "CUSTOMER_LOCATION_SELECTED",
            customerId: customer.id,
            location: selectedLocation,
          },
          window.location.origin
        );
      }
      toastr.options = {
        closeButton: true,
        progressBar: true,
        timeOut: 3000,
        positionClass: "toast-top-right",
      };
      toastr.success(`Location saved for ${customer.name}`);
      setConfirmed(true);
    } catch (err) {
      toastr.error("Failed to save location. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const initials = customer.name
    ? customer.name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((n) => n[0]?.toUpperCase())
        .join("")
    : "?";

  const showingSearch = query.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header: logo + profile of the customer this location is being set for */}
     <WebHeader name={customer.name} />

      {testUser && (
          <div className="max-w-2xl mx-auto px-4 pb-2 pt-2">
              <MembershipStatusCard
                  user={testUser}
                  onView={() => setMembershipOpen(true)}
                  onBuy={() => setMembershipOpen(true)}
              />
          </div>
      )}

      <MembershipPage
          open={membershipOpen}
          onClose={() => setMembershipOpen(false)}
          user={testUser}
          currentProgramId={membershipStatus.programId}
          currentState={membershipStatus.state}
          currentAmount={membershipStatus.planAmount}
          credit={membershipCredit}
          isFirstMembership={membershipStatus.state === "none"}
          onSelect={handleMembershipSelect}
          onSkip={() => setMembershipOpen(false)}
      />

      {/* Hero */}
      <div className="bg-gradient-to-b from-blue-50 to-gray-50 px-4 pt-12 pb-24 flex flex-col items-center">
      
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 text-center">
          Set the location for {customer.name}
        </h1>
        <p className="text-sm text-gray-500 mt-2 mb-8 text-center">
          Search a serviceable city, detect the current location, or pick a popular locality
        </p>

        {/* Split search pill */}
        <div className="relative w-full max-w-2xl" ref={wrapperRef}>
          <div
            className={`flex items-stretch bg-white rounded-full shadow-lg border ${
              isOpen ? "border-[#0B67FC] ring-2 ring-[#0B67FC]/30" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="flex items-center gap-2 pl-5 pr-4 py-3.5 text-left shrink-0 max-w-[45%]"
            >
              <MapPin size={18} className="text-[#0B67FC] shrink-0" />
              <span className="text-sm font-medium text-gray-800 truncate">
                {query || "Search Location"}
              </span>
            </button>
            <div className="w-px bg-gray-200 my-2.5" />
            <input
              type="text"
              value={query}
              onFocus={() => setIsOpen(true)}
              onChange={handleQueryChange}
              placeholder="Search for a city we service..."
              className="flex-1 min-w-0 pl-4 pr-5 py-3.5 text-sm text-gray-600 placeholder:text-gray-400 rounded-r-full outline-none"
            />
          </div>

          {/* Dropdown panel */}
          {isOpen && (
            <div className="absolute md:right-auto w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-10">
              <div className="p-4 max-h-60 overflow-y-auto">
                {!showingSearch && (
                  <button
                    type="button"
                    onClick={handleAutoDetect}
                    disabled={isLocating || !isGoogleLoaded}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-4 rounded-xl border border-[#0B67FC] text-[#0B67FC] font-semibold text-sm hover:bg-blue-50 disabled:opacity-50 transition-colors"
                  >
                    {isLocating ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <LocateFixed size={15} />
                    )}
                    Auto-detect current location
                  </button>
                )}

                {showingSearch ? (
                  // Only cities we actually service, matching what's typed.
                  searchingCities ? (
                    <p className="text-sm text-gray-400 flex items-center gap-2 py-2">
                      <Loader2 size={14} className="animate-spin" /> Searching our cities...
                    </p>
                  ) : citySuggestions.length > 0 ? (
                    <>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        Cities we serve
                      </p>
                      <ul className="divide-y divide-gray-50">
                        {citySuggestions.map((city) => (
                          <li key={city.id}>
                            <button
                              type="button"
                              onClick={() => handleLocalityClick(city)}
                              disabled={localityLoading === city.id || !isGoogleLoaded}
                              className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-blue-50 rounded-lg px-2 -mx-2 transition-colors disabled:opacity-50"
                            >
                              {localityLoading === city.id ? (
                                <Loader2 size={16} className="text-[#0B67FC] animate-spin shrink-0" />
                              ) : (
                                <MapPin size={16} className="text-[#0B67FC] shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{city.cityName}</p>
                                {city.fullRegionName && (
                                  <p className="text-xs text-gray-500 truncate">{city.fullRegionName}</p>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 py-2">
                      We don't service any city matching "{query.trim()}" yet.
                    </p>
                  )
                ) : (
                  <>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {topCitiesLoading ? "Loading cities..." : "Popular localities"}
                    </p>
                    <ul className="divide-y divide-gray-50">
                      {(topCities.length > 0 ? topCities : FALLBACK_LOCALITIES).map((locality) => (
                        <li key={typeof locality === "string" ? locality : locality.id}>
                          <button
                            type="button"
                            onClick={() => handleLocalityClick(locality)}
                            disabled={
                              localityLoading === (typeof locality === "string" ? locality : locality.id) ||
                              !isGoogleLoaded
                            }
                            className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-blue-50 rounded-lg px-2 -mx-2 transition-colors disabled:opacity-50"
                          >
                            {localityLoading === (typeof locality === "string" ? locality : locality.id) ? (
                              <Loader2 size={16} className="text-[#0B67FC] animate-spin shrink-0" />
                            ) : (
                              <MapPin size={16} className="text-[#0B67FC] shrink-0" />
                            )}
                            <span className="text-sm font-medium text-gray-800">
                              {typeof locality === "string" ? locality : locality.cityName}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Checking whether we service this city (auto-detect only) */}
        {checkingService && (
          <div className="w-full max-w-2xl mt-6 p-4 bg-white border border-gray-200 rounded-xl shadow-sm text-sm text-gray-600 flex items-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Checking service availability...
          </div>
        )}

        {/* Not serviceable in this city yet (auto-detect result) */}
        {!checkingService && notServiceable && (
          <div className="w-full max-w-2xl mt-6 p-4 bg-red-50 border border-red-200 rounded-xl shadow-sm text-sm text-red-700">
            We're currently not providing service at <strong>{notServiceable.city}</strong>. Please wait, we're expanding soon!
          </div>
        )}

        {/* Selected location preview + confirm — hidden once confirmed */}
        {selectedLocation && !checkingService && !confirmed && (
          <div className="w-full max-w-2xl mt-6 p-4 bg-white border border-blue-200 rounded-xl shadow-sm">
            <p className="text-sm font-medium text-gray-800">{selectedLocation.displayAddress}</p>
            {selectedLocation.area && (
              <p className="text-xs text-gray-500 mt-1">Area: {selectedLocation.area}</p>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              className="mt-3 w-full bg-[#0B67FC] text-white py-2.5 rounded-xl hover:bg-[#0952d1] disabled:bg-gray-300 transition-colors font-medium"
            >
              {saving ? "Saving..." : "Confirm this location"}
            </button>
          </div>
        )}

        {/* Confirmed: thin summary line with a way back to change it */}
        {confirmed && selectedLocation && (
          <div className="w-full max-w-2xl mt-6 flex items-center gap-2 text-sm text-gray-600">
            <MapPin size={15} className="text-[#0B67FC] shrink-0" />
            <span className="truncate">{selectedLocation.displayAddress}</span>
            <button
              type="button"
              onClick={() => setConfirmed(false)}
              className="ml-auto text-xs font-semibold text-[#0B67FC] hover:underline shrink-0"
            >
              Change
            </button>
          </div>
        )}

        {/* Category strip + discover feed, once the location is confirmed */}
        {confirmed && selectedLocation && (
          <>
            {/* StoreDiscoverList reacts to the redux "selected" category itself -
                onSelect only needs to clear the list on deselect (category === null). */}
            <StoreCategories onSelect={(category) => !category && dispatch(resetStoreDiscover())} />
            <StoreDiscoverList
              city={activeCity}
              lat={selectedLocation.latitude}
              lon={selectedLocation.longitude}
            />
          </>
        )}

        {/* "Popular stores near you" - below the category section, sorted by
            distance (nearest first), independent of category selection. */}
        {confirmed && selectedLocation && (
          <NearbyStoresList lat={selectedLocation.latitude} lon={selectedLocation.longitude} />
        )}
      </div>
    </div>
  );
}